import { useState } from 'react';

interface UploadModalProps {
  onClose: () => void;
}

export default function UploadModal({ onClose }: UploadModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [content, setContent] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [subscriptionLevel, setSubscriptionLevel] = useState('1');
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      processFiles(newFiles);
    }
  };

  const processFiles = (newFiles: File[]) => {
    setFiles(prev => [...prev, ...newFiles]);
    
    // 미리보기 생성
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/') || file.type.startsWith('video/')
    );
    
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (files.length === 0) {
      alert('최소 1개 이상의 파일을 업로드해주세요.');
      return;
    }
    
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    
    console.log({ files, content, hashtags, subscriptionLevel });
    alert('콘텐츠가 업로드되었습니다! 🎉');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-3xl z-10">
          <h2 className="text-2xl font-bold text-gray-800">새 콘텐츠 업로드</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 파일 업로드 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">사진 / 영상</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
                isDragging 
                  ? 'border-pink-500 bg-pink-50' 
                  : 'border-pink-300 hover:border-pink-400'
              }`}
            >
              <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-purple-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-image-add-line text-white text-3xl"></i>
                </div>
                <p className="text-gray-700 font-medium mb-1">파일을 선택하거나 드래그하세요</p>
                <p className="text-sm text-gray-500">사진 또는 영상 (여러 개 선택 가능)</p>
              </label>
            </div>
            
            {/* 미리보기 */}
            {previews.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-3">
                {previews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                      {files[index].type.startsWith('video/') ? (
                        <video
                          src={preview}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="absolute top-2 right-2 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <i className="ri-close-line text-white text-sm"></i>
                    </button>
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="bg-black/70 backdrop-blur-sm rounded px-2 py-1">
                        <p className="text-xs text-white truncate">{files[index].name}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 글 작성 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              내용 <span className="text-pink-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm resize-none"
              rows={6}
              placeholder="팬들에게 전할 내용을 작성하세요...&#10;&#10;예시:&#10;안녕하세요! 오늘 새로운 콘텐츠를 준비했어요 💕&#10;많은 관심 부탁드립니다!"
              required
            />
            <p className="text-xs text-gray-500 mt-2">{content.length}자</p>
          </div>

          {/* 해시태그 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">해시태그</label>
            <input
              type="text"
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm"
              placeholder="#일상 #셀카 #팬아트"
            />
            <p className="text-xs text-gray-500 mt-2">공백으로 구분하여 입력하세요</p>
          </div>

          {/* 구독 레벨 설정 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              구독 레벨 설정 <span className="text-pink-500">*</span>
            </label>
            <select
              value={subscriptionLevel}
              onChange={(e) => setSubscriptionLevel(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-sm cursor-pointer"
            >
              <option value="1">레벨 1 - 무료 (모든 팬)</option>
              <option value="2">레벨 2 - 5,000원 이상 구독자</option>
              <option value="3">레벨 3 - 10,000원 이상 구독자</option>
              <option value="4">레벨 4 - 20,000원 이상 구독자</option>
              <option value="5">레벨 5 - 50,000원 이상 구독자</option>
            </select>
            <p className="text-xs text-gray-500 mt-2">
              <i className="ri-information-line mr-1"></i>
              선택한 레벨 이상의 구독자만 이 콘텐츠를 볼 수 있습니다
            </p>
          </div>

          {/* 버튼 */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all whitespace-nowrap cursor-pointer"
            >
              취소
            </button>
            <button
              type="submit"
              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white py-3 rounded-xl font-semibold hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-upload-cloud-line mr-2"></i>
              업로드
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}